import { useEffect, useState } from 'react';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  title?: string;
  hasPartialData?: boolean;
  showAdditionalFields?: boolean;
  hasCompletedAdditionalFields?: boolean;
}

export default function Modal({ isOpen, onClose, children, title, hasPartialData = false, showAdditionalFields = false, hasCompletedAdditionalFields = false }: ModalProps) {
  const [showExitWarning, setShowExitWarning] = useState(false);
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleCloseAttempt = () => {
    // Se tem dados parciais preenchidos mas não completou os campos adicionais, mostrar aviso
    if (hasPartialData && !hasCompletedAdditionalFields) {
      setShowExitWarning(true);
    } else {
      onClose();
    }
  };

  const handleConfirmExit = () => {
    setShowExitWarning(false);
    onClose();
  };

  const handleCancelExit = () => {
    setShowExitWarning(false);
  };

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleCloseAttempt();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, hasPartialData, hasCompletedAdditionalFields]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black bg-opacity-50 backdrop-blur-sm"
        onClick={handleCloseAttempt}
      ></div>

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-2xl w-full mx-2 sm:mx-4 max-h-[95vh] sm:max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-3 sm:px-6 py-3 sm:py-4 flex items-center justify-between rounded-t-2xl">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
              <i className="ri-whatsapp-line text-green-600 text-xl sm:text-2xl"></i>
            </div>
            <div>
              <h2 className="text-lg sm:text-2xl font-bold text-gray-900">
                {title || 'Quase lá!'}
              </h2>
              <p className="text-xs sm:text-sm text-gray-600">
                Preencha seus dados para receber informações
              </p>
            </div>
          </div>
          <button
            onClick={handleCloseAttempt}
            className="text-gray-400 hover:text-gray-600 transition-colors p-1 sm:p-2 hover:bg-gray-100 rounded-full flex-shrink-0"
          >
            <i className="ri-close-line text-xl sm:text-2xl"></i>
          </button>
        </div>

        {/* Content */}
        <div className="p-3 sm:p-6">
          {children}
        </div>
      </div>

      {/* Exit Warning Modal */}
      {showExitWarning && (
        <div className="absolute inset-0 z-10 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black bg-opacity-70" onClick={handleCancelExit}></div>
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 animate-fadeIn">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-time-line text-yellow-600 text-3xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">
                Não perca seu progresso!
              </h3>
              <p className="text-gray-600 text-lg leading-relaxed">
                Você já preencheu nome e telefone. Faltam apenas <strong className="text-green-600">2 perguntas</strong> que levam menos de <strong className="text-green-600">10 segundos</strong> para completar e receber todas as informações!
              </p>
            </div>
            
            <div className="flex flex-col gap-3">
              <button
                onClick={handleCancelExit}
                className="w-full bg-green-500 hover:bg-green-600 text-white font-bold py-4 px-6 rounded-lg transition-all duration-300 hover:scale-105 shadow-lg flex items-center justify-center gap-2"
              >
                <i className="ri-check-line text-xl"></i>
                Continuar Preenchendo
              </button>
              <button
                onClick={handleConfirmExit}
                className="w-full bg-gray-200 hover:bg-gray-300 text-gray-700 font-semibold py-3 px-6 rounded-lg transition-all duration-300"
              >
                Sair mesmo assim
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
